import * as React from 'react';
import { css } from '@emotion/core';
import Modal from '../layout/modal/Modal';
import ModalHeader from '../layout/modal/ModalHeader';
import { useHistory } from 'react-router-dom';
import { useMsgText } from './hooks';
import { Str } from './translationKeys';
import LocalDiv from './LocalDiv';
import { FLEX_COLUMN, FLEX_ROW } from '../layout/flexBox';
import { useDispatch } from 'react-redux';
import { Language } from './models';
import { useAppState } from '../core/redux/hooks';
import { SELECT } from '../forms/styles/select';
import { setLocaleAction } from './setLocaleAction';
import { TextDirection } from './textDirection';
import { COLORS } from '../globalStyle/colors';
import { btnStyle } from '../forms/styles/button';
import Msg from './Msg';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import Spacer from '../layout/Spacer';


const BTN_STYLE = css`
  width: 145px;
`;

const styles = {
  modal:        [
    FLEX_COLUMN,
    css`
      min-height: 0;
      height: 312px;
      
      ${onMobileDevice}  {
        height: 100vh;
      }
    `,
  ],
  root:         [
    FLEX_COLUMN,
    css`
      /* IE apparently needs this */
      height: 238px;
      flex-basis: 238px;
      align-items: stretch;
    `,
  ],
  select:       [
    SELECT,
    css`
      margin: auto;
      width: 320px;
      color: ${COLORS.DARK_GREY};
    `,
  ],
  buttonRow:    [
    FLEX_ROW,
    css`
      margin: 20px 16px;
      justify-content: flex-end;
    `,
  ],
  submitButton: [
    btnStyle.primary(COLORS.BLUE),
    BTN_STYLE,
  ],
  cancelButton: [
    btnStyle.secondary(COLORS.BLUE),
    BTN_STYLE,
    css`
      background-color: transparent;
    `,
  ],
};


type Props = {
  className?: string;
};


/**
 * ChangeLanguageModal: a component that reads the list of supported languages off the current portal project and allows
 * the user to set their locale from that list of languages.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ChangeLanguageModal: React.FC<Props> = props => {
  const title = useMsgText(Str.ChooseLanguage);

  const history  = useHistory();
  const dispatch = useDispatch();

  const dir = useAppState(state => state.locale.textDirection);
  const currentLang = useAppState(state => state.locale.lang);
  const allLangs    = useAppState(state => state.projectLanguages?.[`${state.projectSummary?.projectId}`] ?? []);

  const [language, setLanguage] = React.useState<Language>(currentLang);

  const dismiss = React.useCallback(() => history.goBack(), [history]);

  const onChange = React.useCallback(
    event => setLanguage(allLangs.find(lang => `${lang.languageId}` === `${event.target.value}`)!),
    [allLangs, setLanguage],
  );

  const onSubmit = React.useCallback(
    () => {
      if (!language) {
        dismiss();
        return;
      }

      dispatch(setLocaleAction(language, language.rtl ? TextDirection.RTL : TextDirection.LTR));
      dismiss();
    },
    [dispatch, dismiss, language],
  );

  return (
    <Modal onDismiss={dismiss} dismissable={true} css={styles.modal}>
      <ModalHeader title={title} onDismiss={dismiss} />
      <LocalDiv css={styles.root}>
        <select onChange={onChange} css={styles.select} dir={dir} value={language.languageId}>
          {
            allLangs.map(lang => <option key={lang.languageId} value={lang.languageId}>{lang.nativeName}</option>)
          }
        </select>
        <LocalDiv css={styles.buttonRow}>
          <button onClick={dismiss} css={styles.cancelButton}><Msg str={Str.Cancel} /></button>
          <Spacer sizePx={12}/>
          <button type='submit' onClick={onSubmit} css={styles.submitButton}><Msg str={Str.Done} /></button>
        </LocalDiv>
      </LocalDiv>
    </Modal>
  );
};

export default ChangeLanguageModal;
